import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import getCallMeBy from '@/components/user/get-call-me-by';
import useUsers from '@/hooks/use-users';
import { cn, pluralize } from '@/lib/utils';
import type { SalesRep } from '@/models/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { SalesRepFilters } from '../types';

const SalesRepFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: salesRepFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: SalesRepFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { salesReps } = useUsers();
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return salesReps.map((rep) => {
      const count = getCount('sales_rep', rep.id);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            <PaddockAvatar
              src={rep.avatar}
              alt={`${rep.first_name} ${rep.last_name}`}
              fallback={`${rep.first_name.charAt(0)} ${rep.last_name.charAt(0)}`}
              className="size-4"
            />
            {getCallMeBy(rep)}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: rep.id,
        icon: (
          <div className="size-4">
            <Checkbox checked={salesRepFilters[rep.id as SalesRep['id']]} />
          </div>
        ),
      };
    });
  }, [salesRepFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Sales Rep...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default SalesRepFiltersComboBoxPopover;
